import React, { useState, useCallback } from 'react';
import { Layout } from 'antd';
import { useTranslation } from 'react-i18next';
import MessageHeader from './MessageHeader';
import MessageContent from './MessageContent';
import MessageDetailsDrawer from './MessageDetailsDrawer';
import MessageFooter from './MessageFooter/MessageFooter';
import MessageInputReplyPreview from './MessageInputReplyPreview/MessageInputReplyPreview';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useMessageFetching from '../hooks/useMessageFetching';
import useContactStatus from '../hooks/useContactStatus';
import useDraftAttachments from '../hooks/useDraftAttachments';
import useMessageSeenTracking from '../hooks/useMessageSeenTracking';
import useSelectedMessageSync from '../hooks/useSelectedMessageSync';
import useMessageScrollManagement from '../hooks/useMessageScrollManagement';
import useMessageSocketEvents from '../hooks/useMessageSocketEvents';
import useContactSocketSync from '../hooks/useContactSocketSync';
import { sendMessage, deleteMessage } from '../MessagesServices';

const Messages = React.memo(({ selectedConversation, onBack, setConversations, setSelectedConversation, screens }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { handleError } = useErrorHandler();

  // ===== Local UI State =====
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  // ===== Custom Hooks =====
  const { draftAttachments, clearDrafts } = useDraftAttachments(selectedConversation);
  const { markMessageAsSeen } = useMessageSeenTracking(selectedConversation);
  const { footerFocusRef, listScrollApiRef, scrollToBottom, focusFooterInput } = useMessageScrollManagement();

  const {
    messages,
    loading,
    hasMore,
    messageRefs,
    handleScroll,
    addMessage,
    updateMessage,
    deleteMessage: removeMessage,
    isInitialLoading,
    isFetchingMoreTop
  } = useMessageFetching(selectedConversation, handleError);

  const {
    isIgnored,
    isSnoozed,
    isAdmin,
    updateContactStatus
  } = useContactStatus(selectedConversation, setConversations, setSelectedConversation);

  // ===== Socket Synchronization =====
  useSelectedMessageSync(messages, selectedMessage, setSelectedMessage, drawerVisible, setDrawerVisible);
  useMessageSocketEvents(selectedConversation, messages, addMessage, updateMessage, removeMessage, markMessageAsSeen);
  useContactSocketSync(selectedConversation, setSelectedConversation, setConversations);

  // ===== Event Handlers =====
  const handleReplyClick = useCallback((replyTo) => {
    const originalMessageId = messages.find(
      m => m.sent_at === replyTo.sent_at && m.sender_type === replyTo.sender_type
    )?.id;

    if (originalMessageId && messageRefs.current[originalMessageId]?.current) {
      messageRefs.current[originalMessageId].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [messages, messageRefs]);

  const handleReplyAction = useCallback((messageItem) => {
    setReplyTo(messageItem);
    focusFooterInput();
  }, [focusFooterInput]);

  const handleSendMessage = useCallback(async (messageData) => {
    scrollToBottom();
    setIsSending(true);

    try {
      await sendMessage(selectedConversation?.id, {
        content: messageData.message,
        attachment_ids: messageData.attachment_ids || [],
        reply_to_message_id: replyTo?.id || undefined
      });

      clearDrafts();
      setReplyTo(null);
      scrollToBottom();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSending(false);
    }
  }, [selectedConversation?.id, handleError, replyTo?.id, scrollToBottom, clearDrafts]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    try {
      await deleteMessage(messageId);
      return Promise.resolve();
    } catch (error) {
      throw error;
    }
  }, []);

  // ===== Styles =====
  const layoutStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
    background: isDarkMode ? '#141414' : '#fff',
    overflow: 'hidden'
  };

  // ===== Render =====
  return (
    <Layout style={layoutStyle}>
      {/* Header */}
      <MessageHeader
        screens={screens}
        onBack={onBack}
        isIgnored={isIgnored}
        isSnoozed={isSnoozed}
        isAdmin={isAdmin}
        updateContactStatus={updateContactStatus}
        selectedConversation={selectedConversation}
      />

      {/* Message List */}
      <MessageContent
        screens={screens}
        selectedConversation={selectedConversation}
        messages={messages}
        loading={loading}
        isInitialLoading={isInitialLoading}
        isFetchingMoreTop={isFetchingMoreTop}
        hasMore={hasMore}
        messageRefs={messageRefs}
        handleScroll={handleScroll}
        handleReplyClick={handleReplyClick}
        onReplyAction={handleReplyAction}
        setSelectedMessage={setSelectedMessage}
        setDrawerVisible={setDrawerVisible}
        scrollApiRef={listScrollApiRef}
      />

      {/* Message Details Drawer */}
      <MessageDetailsDrawer
        screens={screens}
        drawerVisible={drawerVisible}
        selectedMessage={selectedMessage}
        setDrawerVisible={setDrawerVisible}
        setSelectedMessage={setSelectedMessage}
        onDeleteMessage={handleDeleteMessage}
      />

      {/* Reply Preview */}
      <MessageInputReplyPreview
        replyTo={replyTo}
        onCancel={() => setReplyTo(null)}
      />

      {/* Footer Input */}
      <MessageFooter
        onSendMessage={handleSendMessage}
        isSending={isSending}
        focusInputRef={footerFocusRef}
        conversationId={selectedConversation?.id}
        initialFileList={draftAttachments}
      />
    </Layout>
  );
}, (prevProps, nextProps) => (
  prevProps.selectedConversation?.id === nextProps.selectedConversation?.id &&
  prevProps.onBack === nextProps.onBack &&
  prevProps.setConversations === nextProps.setConversations &&
  prevProps.setSelectedConversation === nextProps.setSelectedConversation &&
  prevProps.screens?.xs === nextProps.screens?.xs
));

export default Messages;
